import React, { useState } from 'react'

import { CheckOutlined } from '@ant-design/icons'
import ListPageHeader from '@shared/ListPageHeader'
import ProductSelect from '@shared/products/ProductSelect'
import { Card, Descriptions, Button, Space, Table, Tag, Modal, message, Alert } from 'antd'
import { useTranslation } from 'react-i18next'

import { formatDateTime } from '../../../utils/dateTime'


export default function UnknownProductDetail({
  unknownProduct,
  role = 'admin',
  unknownProductService,
  productService
}) {
  const { t } = useTranslation()
  const [isResolving, setIsResolving] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const [affectedOrdersCount, setAffectedOrdersCount] = useState(0)
  // backfillResult shape mirrors backend: { attempted, success, message }.
  // Surfaced after mapping completes so the user sees whether the platform
  // SKU push succeeded — failure does NOT roll back the mapping itself.
  const [backfillResult, setBackfillResult] = useState(null)

  const performMapping = async (backfillToPlatform) => {
    setIsResolving(true)
    try {
      const response = await unknownProductService.mapTo(
        unknownProduct.id,
        selectedProductId,
        { backfillToPlatform }
      )

      if (response.data) {
        message.success(t('models.unknown_product.map_to_success'))
        setIsCompleted(true)
        setAffectedOrdersCount(
          response.data.affected_orders_count ?? response.data.affected_order_ids?.length ?? 0
        )
        setBackfillResult(response.data.backfill || null)
      }
    } catch (error) {
      // 不直接顯示 error.message — 後端 errorId / 內部 stack 不該暴露給使用者，
      // 一律顯示通用文案，細節留 console / Sentry。前端不做啟發式錯誤分類。
      // eslint-disable-next-line no-console
      console.error('[UnknownProductDetail] mapTo failed:', error)
      message.error(t('common.error.operation_failed'))
    } finally {
      setIsResolving(false)
    }
  }

  // Two-step confirm so a missed click can't push a stale SKU to the seller's
  // listing on Shopee. The platform-backfill prompt only appears when the
  // backend reports backfill_supported (per-platform check, currently Shopee).
  const handleMapTo = () => {
    if (!selectedProductId) {
      message.warning(t('models.unknown_product.select_product'))
      return
    }

    Modal.confirm({
      title: t('models.unknown_product.map_to_confirm'),
      content: t('models.unknown_product.affected_orders_will_update', {
        count: unknownProduct.pending_orders_count
      }),
      onOk: async () => {
        if (!unknownProduct.backfill_supported) {
          await performMapping(false)
          return
        }

        Modal.confirm({
          title: t('models.unknown_product.backfill_to_platform_confirm'),
          content: t('models.unknown_product.backfill_to_platform_description', {
            platform: unknownProduct.platform || ''
          }),
          okText: t('common.yes'),
          cancelText: t('common.no'),
          onOk: () => performMapping(true),
          onCancel: () => performMapping(false)
        })
      }
    })
  }

  // 訂單列表 columns
  const orderColumns = [
    {
      title: t('models.order.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <a
          href={`/${role}/orders/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {name}
        </a>
      )
    },
    {
      title: t('models.order.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'pending' ? 'error' : 'default'}>
          {t(`enums.order.status.${status}`)}
        </Tag>
      )
    },
    {
      title: t('models.order.receiver_name'),
      dataIndex: 'receiver_name',
      key: 'receiver_name'
    },
    {
      title: t('models.unknown_product.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatDateTime(date)
    }
  ]

  const headerBasePath = role === 'operator' ? '/operator' : '/admin'

  // Backfill result alert — three states map to three display variants. We
  // intentionally render BELOW the success alert so the mapping outcome is
  // always the headline, with the platform sync as secondary information.
  //
  // 重要：失敗訊息一律走 error_code → i18n key 翻譯，不直接顯示後端 message 字串。
  // 後端契約（BaseProducts#backfill_sku）：{ success, error_code, interpolations }。
  // 未知 error_code（後端新增了前端尚未有翻譯的 code）退回 generic 文案。
  const renderBackfillAlert = () => {
    if (!backfillResult || !backfillResult.attempted) return null
    if (backfillResult.success) {
      return (
        <Alert
          message={t('models.unknown_product.backfill_success')}
          description={t('models.unknown_product.backfill_success_description', {
            platform: unknownProduct.platform || ''
          })}
          type="success"
          showIcon
          style={{ marginTop: 16 }}
        />
      )
    }

    const code = backfillResult.error_code || 'unexpected'
    const interpolationDefaults = { platform: unknownProduct.platform || '' }
    const interpolations = { ...interpolationDefaults, ...(backfillResult.interpolations || {}) }
    // i18next 找不到 key 時 fallback 到 generic 那條
    const codeKey = `models.unknown_product.backfill_failed_codes.${code}`
    const description = t(codeKey, {
      ...interpolations,
      defaultValue: t('models.unknown_product.backfill_failed_codes.unexpected', interpolations)
    })

    return (
      <Alert
        message={t('models.unknown_product.backfill_failed')}
        description={description}
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
      />
    )
  }

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.unknown', { label: unknownProduct.display_identifier }]}
      />
      <Card
        title={t('models.unknown_product._')}
        style={{ marginBottom: 16 }}
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('models.unknown_product.display_identifier')}>
            {unknownProduct.display_identifier}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.sku')}>
            {unknownProduct.sku || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.name')}>
            {unknownProduct.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.merchant')}>
            {unknownProduct.merchant?.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.platform')}>
            {unknownProduct.platform || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.ec_account')}>
            {unknownProduct.ec_account_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.pending_orders_count')}>
            <Tag color={unknownProduct.pending_orders_count > 0 ? 'error' : 'default'}>
              {unknownProduct.pending_orders_count}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.unknown_product.total_quantity')}>
            {unknownProduct.total_quantity}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(unknownProduct.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.updated_at')}>
            {formatDateTime(unknownProduct.updated_at)}
          </Descriptions.Item>
        </Descriptions>

        {/* 對應完成提示 */}
        {isCompleted && (
          <>
            <Alert
              message={t('models.unknown_product.map_to_completed')}
              description={t('models.unknown_product.map_to_completed_description', {
                count: affectedOrdersCount
              })}
              type="success"
              showIcon
              style={{ marginTop: 24 }}
            />
            {renderBackfillAlert()}
          </>
        )}

        {/* 商品選擇區塊 - 完成後隱藏 */}
        {!isCompleted && (
          <div style={{ marginTop: 24 }}>
            <Space style={{ marginBottom: 16 }}>
              <ProductSelect
                productService={productService}
                value={selectedProductId}
                onChange={setSelectedProductId}
                placeholder={t('models.unknown_product.select_product')}
                merchantId={unknownProduct.merchant?.id}
                style={{ width: 300 }}
              />
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleMapTo}
                loading={isResolving}
                disabled={!selectedProductId}
              >
                {t('models.unknown_product.map_to')}
              </Button>
            </Space>
          </div>
        )}
      </Card>

      <Card
        title={t('models.unknown_product.affected_orders')}
      >
        <Table
          columns={orderColumns}
          dataSource={unknownProduct.affected_orders || []}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  )
}
